import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface TheveninDiagramProps {
    vTh: number;
    rTh: number;
    loadResistance?: number;
    showEquivalent: boolean; // If false, show complex? Or just toggle logic external?
    // Assuming this component JUST renders the Thevenin equivalent.
    // Complex network is too variable to hardcode here efficiently without more data.
}

export const TheveninDiagram: React.FC<TheveninDiagramProps> = ({
    vTh,
    rTh,
    loadResistance
}) => {
    // Layout: Series Vth + Rth -> Terminals A, B -> (Optional Load)

    const width = 450;
    const height = 250;
    const padding = 50;
    const centerY = height / 2;
    // Usually Thevenin is a 2-terminal black box.
    // Let's draw: (Vth)--[Rth]--o A
    //              |            |
    //              +------------o B

    const topY = centerY - 50;
    const bottomY = centerY + 50;

    // Wait, simpler linear layout:
    // A o---[Rth]---(Vth)---o B is not quite right.
    // Standard:
    //      o A
    //      |
    //     [Rth]
    //      |
    //     (Vth)
    //      |
    //      o B
    // Or Horizontal:
    //      A o----[Rth]----(Vth)----o B (Not standard loop)

    // Let's use the standard "Thevenin Equivalent Circuit" loop view looking into terminals A-B.
    // Usually it's:
    //      _______[Rth]_______ A
    //     |
    //    (Vth)
    //     |___________________ B

    const elements: CircuitElement[] = [];
    const junctions = [];

    const leftVoltsX = padding + 60;
    const rightTermX = width - padding - 60;

    // Voltage Source (Left Vertical)
    elements.push({
        type: 'voltageSource',
        from: { x: leftVoltsX, y: bottomY },
        to: { x: leftVoltsX, y: topY },
        value: vTh,
        label: `Vth=${vTh}V`,
        labelSide: 'left'
    });

    // Top Wire + Resistor (Rth on top rail)
    elements.push({
        type: 'resistor',
        from: { x: leftVoltsX, y: topY },
        to: { x: rightTermX, y: topY },
        value: rTh,
        label: `Rth=${rTh}Ω`,
        labelSide: 'left' // Top
    });

    // Bottom Wire
    elements.push({
        type: 'wire',
        from: { x: leftVoltsX, y: bottomY },
        to: { x: rightTermX, y: bottomY },
        labelSide: 'left'
    });

    // Terminals A and B
    junctions.push({ x: rightTermX, y: topY, label: 'A', labelPosition: 'right' as const });
    junctions.push({ x: rightTermX, y: bottomY, label: 'B', labelPosition: 'right' as const });

    // Load Resistor if present
    if (loadResistance) {
        elements.push({
            type: 'resistor',
            from: { x: rightTermX, y: topY },
            to: { x: rightTermX, y: bottomY },
            value: loadResistance,
            label: `RL=${loadResistance}Ω`,
            labelSide: 'right'
        });
    }

    return (
        <CircuitRenderer width={width} height={height} elements={elements} junctions={junctions} />
    );
};
